/**
 * Options tab — two step cards: send defaults and overrides (a slot's own
 * amount; Phase 18b — a list's own amount, gateway and send options).
 * Auto-send has its own tab (Phase 20).
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC, useMemo } from 'react';
import { ListChecks, SlidersHorizontal } from 'lucide-react';

import { StepCard, StepCardGroup } from '@/components/ui';

import { computeContainerTimes, slotKey } from '../../../utils/slot-times';
import { InheritedSection } from '../master/InheritedSection';
import type { ContainerEditor } from '../state/useContainerEditor';
import { SendDefaultsStep } from './SendDefaultsStep';
import { OverridesStep } from './OverridesStep';

interface Props {
  editor: ContainerEditor;
  countryKey: string;
}

export const OptionsTab: FC<Props> = ({ editor, countryKey }) => {
  const { draft } = editor;
  const d = draft.sendDefaults;

  const summary = [
    d.forceSend ? 'duplicate on' : 'duplicate off',
    `TLD ${d.shortLinkTldValue ?? 'auto'}`,
    d.aiOptimizationEnabled
      ? 'AI on'
      : d.batchAmount
        ? `batches ${d.batchAmount}×${d.batchDelay ?? '—'}m`
        : 'everything else default',
  ].join(' · ');

  const overrideCount = draft.slots.filter((s) => s.amount !== null).length;
  const listCount = draft.listOverrides.length;

  const timesByKey = useMemo(() => {
    const times =
      computeContainerTimes(
        [
          {
            id: 'draft',
            enabled: true,
            startMode: draft.startMode,
            gapMin: draft.gapMin,
            startAtMin: draft.startAtMin,
            endAtMin: draft.endAtMin,
            intervalMin: draft.intervalMin,
            slots: draft.slots.map((s, i) => ({
              id: slotKey(s, i),
              enabled: s.enabled,
              pinnedAtMin: s.pinnedAtMin,
            })),
          },
        ],
        { startMin: 0, endMin: 0 }, // only the minutes are read here — the whole day, on the Danish clock (no country day)
      ).get('draft')?.slotTimes ?? [];
    return new Map(times.map((t) => [t.slotId ?? '', t]));
  }, [draft]);

  return (
    <StepCardGroup>
      <StepCard
        index={1}
        icon={SlidersHorizontal}
        title="Send defaults"
        summary={summary}
      >
        <InheritedSection section="options">
          <SendDefaultsStep editor={editor} countryKey={countryKey} />
        </InheritedSection>
      </StepCard>
      <StepCard
        index={2}
        icon={ListChecks}
        title="Overrides"
        summary={
          [
            overrideCount > 0 ? `${overrideCount} slot amount${overrideCount > 1 ? 's' : ''}` : null,
            listCount > 0 ? `${listCount} list${listCount > 1 ? 's' : ''}` : null,
          ].filter(Boolean).join(' · ') || 'none'
        }
      >
        <OverridesStep editor={editor} timesByKey={timesByKey} countryKey={countryKey} />
      </StepCard>
    </StepCardGroup>
  );
};
